/**
 * HelpCoverageBadge — tiny pill showing tooltip coverage across the app.
 *
 * Computes coverage as:
 *   coveredControls / expectedControls
 * where expectedControls = sum of controls per form in the Known Forms Registry
 * (localStorage), and coveredControls = total rows in AppHelpText.
 *
 * Admin-only. Click to expand a breakdown modal.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { BookOpen, X, CheckCircle, Circle } from 'lucide-react';
import { API_BASE as API_URL } from '../../lib/api';

const PROJECT = 'RetailERP';
const REG_KEY = 'retailcliq_help_registry';

function loadRegistry() {
  try { return JSON.parse(localStorage.getItem(REG_KEY) || '{}'); } catch { return {}; }
}

export function HelpCoverageBadge({ userRole = '' }) {
  // Read adminFeatures from global context storage
  const adminFeaturesForBadge = (() => {
    try {
      const raw = localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context') || localStorage.getItem('retailcliq_global_context') || sessionStorage.getItem('retailcliq_global_context');
      if (raw) {
        const ctx = JSON.parse(raw);
        return Array.isArray(ctx?.adminFeatures) ? ctx.adminFeatures : [];
      }
    } catch (_) {}
    return [];
  })();
  const isAdmin =
    userRole === 'SUPER_ADMIN' || userRole === 'sys-admin' || userRole === 'superAdmin' ||
    ((userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR') && adminFeaturesForBadge.includes('DYNAMIC_ATTRIBUTES'));

  const [coverage, setCoverage] = useState({ byForm: {}, total: 0 });
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/help/coverage/${encodeURIComponent(PROJECT)}`);
        const j = await r.json();
        if (!cancelled) setCoverage({ byForm: j.byForm || {}, total: j.total || 0 });
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000); // refresh once a minute
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAdmin, tick]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const registry = useMemo(() => (open ? loadRegistry() : {}), [open, tick]);

  const stats = useMemo(() => {
    const reg = loadRegistry();
    let expected = 0;
    Object.values(reg).forEach((v) => { expected += (v?.controls?.length || 0); });
    const covered = Math.min(coverage.total, expected || coverage.total);
    const pct = expected > 0 ? Math.round((covered / expected) * 100) : (coverage.total > 0 ? 100 : 0);
    return { expected, covered, pct };
  }, [coverage]);

  if (!isAdmin) return null;

  const color = stats.pct >= 85 ? '#16a34a' : stats.pct >= 50 ? '#ca8a04' : '#dc2626';
  const bg = stats.pct >= 85 ? '#dcfce7' : stats.pct >= 50 ? '#fef3c7' : '#fee2e2';

  return (
    <>
      <button
        data-testid="help-coverage-badge"
        onClick={() => { setTick((t) => t + 1); setOpen(true); }}
        title="AI tooltip coverage across the app"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 9px', borderRadius: 999, border: `1px solid ${color}`,
          background: bg, color, cursor: 'pointer',
          fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <BookOpen size={11} />
        {stats.expected > 0 ? `${stats.pct}% tooltips` : `${coverage.total} tooltips`}
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 99991, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 520, maxHeight: '78vh', background: '#fff', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,0.25)', fontFamily: "'Inter', system-ui, sans-serif" }}
          >
            <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13 }}>
                <BookOpen size={14} /> Tooltip Coverage
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: '#e0e7ff', cursor: 'pointer' }}><X size={16} /></button>
            </div>

            <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 32, fontWeight: 800, color, fontFamily: "'Inter', system-ui" }}>{stats.expected > 0 ? `${stats.pct}%` : '—'}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  {stats.expected > 0
                    ? <>{coverage.total} saved of {stats.expected} known controls</>
                    : <>{coverage.total} tooltips saved (open more forms to estimate coverage)</>}
                </div>
              </div>
              <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${stats.pct}%`, height: '100%', background: color, transition: 'width 300ms ease' }} />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px', fontSize: 12, color: '#0f172a' }}>
              {Object.keys(registry).length === 0 && Object.keys(coverage.byForm).length === 0 ? (
                <div style={{ padding: 24, color: '#64748b', textAlign: 'center' }}>
                  No forms registered yet. Navigate through the app — every form you visit is registered automatically.
                </div>
              ) : (
                Object.keys({ ...registry, ...coverage.byForm })
                  .sort()
                  .map((form) => {
                    const expected = registry[form]?.controls?.length || 0;
                    const saved = coverage.byForm[form] || 0;
                    const done = expected > 0 && saved >= expected;
                    const pct = expected > 0 ? Math.min(100, Math.round((saved / expected) * 100)) : (saved > 0 ? 100 : 0);
                    return (
                      <div key={form} style={{ padding: '8px 6px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                        {done ? <CheckCircle size={13} color="#16a34a" /> : <Circle size={13} color="#94a3b8" />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{form}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>
                            {saved}/{expected || '?'} {expected ? `(${pct}%)` : 'saved'}
                          </div>
                        </div>
                        <div style={{ width: 80, height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: done ? '#16a34a' : pct > 0 ? '#f59e0b' : '#cbd5e1' }} />
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HelpCoverageBadge;
